import { useCostDashboard } from "@/hooks/useCostDashboard";
import { useCommitments } from "@/hooks/useCommitments";
import {
  Badge,
  Box,
  Button,
  Card,
  Group,
  Modal,
  NumberInput,
  Paper,
  Progress,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconCheck,
  IconPlus,
  IconReceipt,
  IconUser,
  IconWallet,
} from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/projects/$projectId/money" as any)({
  component: MoneyPage,
});

function MoneyPage() {
  const params = Route.useParams();
  const projectId = "projectId" in params ? params.projectId : "";
  const navigate = useNavigate();
  const { data: dashboardData, isLoading: dashboardLoading } = useCostDashboard(projectId);
  const { data: commitmentsData, isLoading: commitmentsLoading } = useCommitments(projectId);

  const [addExpenseOpened, { open: openAddExpense, close: closeAddExpense }] = useDisclosure(false);
  const [recordPaymentOpened, { open: openRecordPayment, close: closeRecordPayment }] = useDisclosure(false);
  const [selectedPayee, setSelectedPayee] = useState<string | null>(null);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const isLoading = dashboardLoading || commitmentsLoading;

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={140} radius="md" />
        <Skeleton height={300} radius="md" />
        <Skeleton height={300} radius="md" />
      </Stack>
    );
  }

  const summary = dashboardData?.summary || {
    totalBudget: 0,
    totalCommitted: 0,
    totalActuals: 0,
    totalVariance: 0,
    percentCommitted: 0,
    percentSpent: 0,
    healthStatus: "healthy" as const,
    costCodeCount: 0,
    atBudgetCount: 0,
    overBudgetCount: 0,
    atRiskAmount: 0,
  };

  const commitments = commitmentsData?.commitments || [];

  // Calculate the three key numbers
  const budget = summary.totalBudget;
  const spent = summary.totalActuals;
  const left = budget - spent;
  const percentUsed = budget > 0 ? (spent / budget) * 100 : 0;

  // Determine overall status
  const getOverallStatus = () => {
    if (summary.overBudgetCount > 0) return { label: "Over Budget", color: "red", icon: IconAlertTriangle };
    if (summary.atBudgetCount > 0 || percentUsed > 90) return { label: "Watch It", color: "yellow", icon: IconAlertTriangle };
    return { label: "On Track", color: "green", icon: IconCheck };
  };

  const status = getOverallStatus();

  // Group commitments by payee (vendor)
  const payeeGroups = commitments.reduce((acc, c) => {
    const payee = c.vendorName || "Unknown";
    if (!acc[payee]) {
      acc[payee] = {
        name: payee,
        agreed: 0,
        paid: 0,
        items: [],
      };
    }
    acc[payee].agreed += c.amount;
    // For now, we consider "paid" status as fully paid
    if (c.status === "paid") {
      acc[payee].paid += c.amount;
    }
    acc[payee].items.push(c);
    return acc;
  }, {} as Record<string, { name: string; agreed: number; paid: number; items: typeof commitments }>);

  const payeeList = Object.values(payeeGroups).sort((a, b) => b.agreed - a.agreed);

  // Group by category (using cost codes simplified)
  const categoryGroups = dashboardData?.byCostCode?.reduce((acc, cc) => {
    // Simplify division names to categories
    const division = cc.division?.split(" ").slice(1).join(" ") || "General";
    const category = simplifyCategory(division);

    if (!acc[category]) {
      acc[category] = {
        name: category,
        budget: 0,
        spent: 0,
        committed: 0,
      };
    }
    acc[category].budget += cc.budget;
    acc[category].spent += cc.actuals;
    acc[category].committed += cc.committed;
    return acc;
  }, {} as Record<string, { name: string; budget: number; spent: number; committed: number }>) || {};

  const categoryList = Object.values(categoryGroups).sort((a, b) => b.budget - a.budget);

  // Total still owed
  const totalOwed = payeeList.reduce((sum, p) => sum + (p.agreed - p.paid), 0);

  const hasBudget = budget > 0;

  return (
    <Stack gap="lg">
      {/* THREE-NUMBER HEADER - The Core Simplification */}
      <Paper p="xl" withBorder radius="md" style={{ background: "linear-gradient(135deg, var(--mantine-color-blue-0) 0%, var(--mantine-color-indigo-0) 100%)" }}>
        <Stack gap="md">
          <Group justify="space-around" wrap="wrap">
            <Box ta="center" style={{ minWidth: 120 }}>
              <Text size="sm" c="dimmed" fw={500}>Budget</Text>
              <Text size="xl" fw={700} style={{ fontSize: "1.75rem" }}>
                {formatCurrency(budget)}
              </Text>
            </Box>
            <Box ta="center" style={{ minWidth: 120 }}>
              <Text size="sm" c="dimmed" fw={500}>Spent</Text>
              <Text size="xl" fw={700} style={{ fontSize: "1.75rem" }}>
                {formatCurrency(spent)}
              </Text>
            </Box>
            <Box ta="center" style={{ minWidth: 120 }}>
              <Text size="sm" c="dimmed" fw={500}>Left</Text>
              <Text
                size="xl"
                fw={700}
                c={left >= 0 ? "green" : "red"}
                style={{ fontSize: "1.75rem" }}
              >
                {formatCurrency(Math.abs(left))}
                {left < 0 && " over"}
              </Text>
            </Box>
          </Group>

          <Progress
            value={Math.min(percentUsed, 100)}
            size="lg"
            color={percentUsed > 100 ? "red" : percentUsed > 90 ? "yellow" : "blue"}
            radius="md"
          />

          <Group justify="center" gap="xs">
            <ThemeIcon size="sm" color={status.color} variant="light" radius="xl">
              <status.icon size={12} />
            </ThemeIcon>
            <Text ta="center" c="dimmed" size="sm">
              {percentUsed.toFixed(0)}% of budget used • <Text span fw={600} c={status.color}>{status.label}</Text>
            </Text>
          </Group>
        </Stack>
      </Paper>

      {!hasBudget ? (
        /* Empty State - Friendly Onboarding */
        <Paper p="xl" withBorder style={{ borderStyle: "dashed" }}>
          <Stack align="center" gap="lg" py="xl">
            <ThemeIcon size={80} variant="light" color="blue" radius="xl">
              <IconWallet size={40} />
            </ThemeIcon>

            <Stack gap="xs" align="center">
              <Title order={2}>Track Your Money</Title>
              <Text c="dimmed" ta="center" maw={500} size="md">
                Add your expenses to see where your money is going.
                It's as simple as writing down who you're paying and how much.
              </Text>
            </Stack>

            <Button
              size="lg"
              leftSection={<IconPlus size={20} />}
              onClick={openAddExpense}
            >
              Add Your First Expense
            </Button>

            <Text size="xs" c="dimmed" ta="center" maw={400}>
              Or if you have an existing budget, you can set it up in the{" "}
              <Text
                span
                c="blue"
                style={{ cursor: "pointer" }}
                onClick={() => navigate({ to: "/projects/$projectId/budget", params: { projectId } })}
              >
                Budget settings
              </Text>
            </Text>
          </Stack>
        </Paper>
      ) : (
        <>
          {/* WHERE YOUR MONEY IS GOING */}
          <Paper p="xl" withBorder>
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <div>
                  <Title order={3}>Where Your Money Is Going</Title>
                  <Text size="sm" c="dimmed">Expenses by category</Text>
                </div>
                <Button
                  variant="light"
                  leftSection={<IconPlus size={16} />}
                  onClick={openAddExpense}
                >
                  Add Expense
                </Button>
              </Group>

              {categoryList.length === 0 ? (
                <Paper p="lg" withBorder style={{ borderStyle: "dashed" }}>
                  <Text c="dimmed" ta="center">
                    No expenses yet. Click "Add Expense" to start tracking.
                  </Text>
                </Paper>
              ) : (
                <Stack gap="sm">
                  {categoryList.map((cat) => {
                    const catPercent = cat.budget > 0 ? (cat.spent / cat.budget) * 100 : 0;
                    const isOver = cat.spent > cat.budget;
                    const remaining = cat.budget - cat.spent;

                    return (
                      <Card key={cat.name} withBorder padding="md">
                        <Group justify="space-between" mb="xs">
                          <Group gap="sm">
                            <ThemeIcon variant="light" color={getCategoryColor(cat.name)} size="lg">
                              {getCategoryIcon(cat.name)}
                            </ThemeIcon>
                            <div>
                              <Text fw={600}>{cat.name}</Text>
                              <Text size="xs" c="dimmed">
                                {formatCurrency(cat.spent)} of {formatCurrency(cat.budget)}
                              </Text>
                            </div>
                          </Group>
                          <div style={{ textAlign: "right" }}>
                            {isOver ? (
                              <Badge color="red" variant="light">
                                {formatCurrency(Math.abs(remaining))} over
                              </Badge>
                            ) : cat.spent >= cat.budget ? (
                              <Badge color="green" variant="light">
                                <IconCheck size={12} style={{ marginRight: 4 }} />
                                Complete
                              </Badge>
                            ) : (
                              <Text size="sm" c="dimmed">
                                {formatCurrency(remaining)} left
                              </Text>
                            )}
                          </div>
                        </Group>
                        <Progress
                          value={Math.min(catPercent, 100)}
                          color={isOver ? "red" : catPercent > 90 ? "yellow" : "blue"}
                          size="sm"
                        />
                      </Card>
                    );
                  })}
                </Stack>
              )}
            </Stack>
          </Paper>

          {/* WHO YOU'RE PAYING */}
          <Paper p="xl" withBorder>
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <div>
                  <Title order={3}>Who You're Paying</Title>
                  <Text size="sm" c="dimmed">Your contractors and vendors</Text>
                </div>
                <Button
                  variant="light"
                  leftSection={<IconPlus size={16} />}
                  onClick={openAddExpense}
                >
                  Add Person/Company
                </Button>
              </Group>

              {payeeList.length === 0 ? (
                <Paper p="lg" withBorder style={{ borderStyle: "dashed" }}>
                  <Text c="dimmed" ta="center">
                    No contractors or vendors added yet.
                  </Text>
                </Paper>
              ) : (
                <Stack gap="sm">
                  {payeeList.map((payee) => {
                    const owed = payee.agreed - payee.paid;
                    const isPaidInFull = owed <= 0;
                    const payPercent = payee.agreed > 0 ? (payee.paid / payee.agreed) * 100 : 0;

                    return (
                      <Card
                        key={payee.name}
                        withBorder
                        padding="md"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setSelectedPayee(payee.name);
                          openRecordPayment();
                        }}
                      >
                        <Group justify="space-between" wrap="nowrap">
                          <Group gap="sm" style={{ flex: 1 }}>
                            <ThemeIcon variant="light" color="gray" size="lg" radius="xl">
                              <IconUser size={18} />
                            </ThemeIcon>
                            <div style={{ flex: 1 }}>
                              <Group justify="space-between" mb={4}>
                                <Text fw={600}>{payee.name}</Text>
                                {isPaidInFull ? (
                                  <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>
                                    Paid in full
                                  </Badge>
                                ) : (
                                  <Text size="sm" c="orange" fw={500}>
                                    Owe {formatCurrency(owed)}
                                  </Text>
                                )}
                              </Group>
                              <Group gap="xs">
                                <Text size="xs" c="dimmed">
                                  Agreed: {formatCurrency(payee.agreed)}
                                </Text>
                                <Text size="xs" c="dimmed">•</Text>
                                <Text size="xs" c="dimmed">
                                  Paid: {formatCurrency(payee.paid)}
                                </Text>
                              </Group>
                              <Progress
                                value={payPercent}
                                color={isPaidInFull ? "green" : "blue"}
                                size="xs"
                                mt={6}
                              />
                            </div>
                          </Group>
                        </Group>
                      </Card>
                    );
                  })}
                </Stack>
              )}

              {/* Total Still Owed */}
              {totalOwed > 0 && (
                <Paper p="md" withBorder bg="orange.0">
                  <Group justify="space-between">
                    <Text fw={600}>Total Still Owed</Text>
                    <Text fw={700} size="lg" c="orange">
                      {formatCurrency(totalOwed)}
                    </Text>
                  </Group>
                </Paper>
              )}
            </Stack>
          </Paper>

          {/* Warning Banner */}
          {summary.overBudgetCount > 0 && (
            <Paper p="md" withBorder bg="red.0" style={{ borderColor: "var(--mantine-color-red-3)" }}>
              <Group gap="sm">
                <ThemeIcon color="red" variant="light">
                  <IconAlertTriangle size={18} />
                </ThemeIcon>
                <div>
                  <Text fw={600} c="red.8">Heads up!</Text>
                  <Text size="sm" c="red.7">
                    {summary.overBudgetCount} categor{summary.overBudgetCount > 1 ? "ies are" : "y is"} over budget
                    by a total of {formatCurrency(summary.atRiskAmount)}.
                  </Text>
                </div>
              </Group>
            </Paper>
          )}

          {/* Quick Actions */}
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Button
              variant="light"
              size="lg"
              leftSection={<IconReceipt size={20} />}
              onClick={openRecordPayment}
            >
              Record a Payment
            </Button>
            <Button
              variant="light"
              size="lg"
              leftSection={<IconPlus size={20} />}
              onClick={openAddExpense}
            >
              Add New Expense
            </Button>
          </SimpleGrid>
        </>
      )}

      {/* Add Expense Modal - Simple Mode */}
      <AddExpenseModal
        opened={addExpenseOpened}
        onClose={closeAddExpense}
        projectId={projectId}
      />

      {/* Record Payment Modal */}
      <RecordPaymentModal
        opened={recordPaymentOpened}
        onClose={closeRecordPayment}
        projectId={projectId}
        payees={payeeList}
        selectedPayee={selectedPayee}
      />
    </Stack>
  );
}

// Helper functions
function simplifyCategory(division: string): string {
  const lower = division.toLowerCase();
  if (lower.includes("kitchen") || lower.includes("cabinet") || lower.includes("appliance")) return "Kitchen";
  if (lower.includes("bath") || lower.includes("plumb")) return "Bathroom & Plumbing";
  if (lower.includes("electric")) return "Electrical";
  if (lower.includes("floor") || lower.includes("carpet") || lower.includes("tile")) return "Flooring";
  if (lower.includes("paint") || lower.includes("finish")) return "Paint & Finishes";
  if (lower.includes("roof")) return "Roofing";
  if (lower.includes("window") || lower.includes("door")) return "Windows & Doors";
  if (lower.includes("hvac") || lower.includes("heat") || lower.includes("air")) return "HVAC";
  if (lower.includes("landscape") || lower.includes("outdoor")) return "Outdoor";
  if (lower.includes("concrete") || lower.includes("masonry") || lower.includes("foundation")) return "Foundation & Concrete";
  if (lower.includes("frame") || lower.includes("wood") || lower.includes("lumber")) return "Framing & Structure";
  return division || "General";
}

function getCategoryColor(category: string): string {
  const colors: Record<string, string> = {
    "Kitchen": "orange",
    "Bathroom & Plumbing": "blue",
    "Electrical": "yellow",
    "Flooring": "teal",
    "Paint & Finishes": "grape",
    "Roofing": "red",
    "Windows & Doors": "cyan",
    "HVAC": "indigo",
    "Outdoor": "green",
    "Foundation & Concrete": "gray",
    "Framing & Structure": "brown",
  };
  return colors[category] || "blue";
}

function getCategoryIcon(category: string) {
  // Simple emoji-based icons
  const icons: Record<string, string> = {
    "Kitchen": "🍳",
    "Bathroom & Plumbing": "🚿",
    "Electrical": "⚡",
    "Flooring": "🪵",
    "Paint & Finishes": "🎨",
    "Roofing": "🏠",
    "Windows & Doors": "🚪",
    "HVAC": "❄️",
    "Outdoor": "🌳",
    "Foundation & Concrete": "🧱",
    "Framing & Structure": "🔨",
  };
  return <span style={{ fontSize: 16 }}>{icons[category] || "📦"}</span>;
}

// Add Expense Modal Component
interface AddExpenseModalProps {
  opened: boolean;
  onClose: () => void;
  projectId: string;
}

function AddExpenseModal({ opened, onClose, projectId }: AddExpenseModalProps) {
  const navigate = useNavigate();

  const form = useForm({
    initialValues: {
      name: "",
      category: "",
      amount: "",
      payee: "",
      paymentStatus: "not_yet" as "not_yet" | "partial" | "paid",
      paidAmount: "",
    },
    validate: {
      name: (v) => v.length < 1 ? "What is this expense for?" : null,
      amount: (v) => !v ? "How much is it?" : null,
    },
  });

  const handleSubmit = form.onSubmit(() => {
    // For now, redirect to the full budget/commitment flow
    // In a future iteration, this would create a simplified expense record
    notifications.show({
      title: "Coming Soon!",
      message: "Simple expense tracking is being built. For now, let's use the detailed budget tools.",
      color: "blue",
    });
    onClose();
    navigate({ to: "/projects/$projectId/budget", params: { projectId } });
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Add Expense"
      size="md"
      centered
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <TextInput
            label="What is this for?"
            placeholder="e.g., Kitchen Cabinets, Flooring"
            {...form.getInputProps("name")}
            required
          />

          <Select
            label="Category"
            placeholder="Select category"
            data={[
              { value: "kitchen", label: "Kitchen" },
              { value: "bathroom", label: "Bathroom" },
              { value: "flooring", label: "Flooring" },
              { value: "electrical", label: "Electrical" },
              { value: "plumbing", label: "Plumbing" },
              { value: "paint", label: "Paint & Finishes" },
              { value: "outdoor", label: "Outdoor" },
              { value: "other", label: "Other" },
            ]}
            {...form.getInputProps("category")}
          />

          <NumberInput
            label="How much?"
            placeholder="0"
            prefix="$"
            thousandSeparator=","
            min={0}
            {...form.getInputProps("amount")}
            required
          />

          <TextInput
            label="Who are you paying? (optional)"
            placeholder="e.g., Mike's Flooring, Home Depot"
            {...form.getInputProps("payee")}
          />

          <Select
            label="Have you paid yet?"
            data={[
              { value: "not_yet", label: "Not yet - just planning" },
              { value: "partial", label: "Partially - paid some" },
              { value: "paid", label: "Yes - paid in full" },
            ]}
            {...form.getInputProps("paymentStatus")}
          />

          {form.values.paymentStatus === "partial" && (
            <NumberInput
              label="How much have you paid so far?"
              placeholder="0"
              prefix="$"
              thousandSeparator=","
              min={0}
              {...form.getInputProps("paidAmount")}
            />
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              Add Expense
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

// Record Payment Modal Component
interface PayeeInfo {
  name: string;
  agreed: number;
  paid: number;
}

interface RecordPaymentModalProps {
  opened: boolean;
  onClose: () => void;
  projectId: string;
  payees: PayeeInfo[];
  selectedPayee: string | null;
}

function RecordPaymentModal({ opened, onClose, projectId, payees, selectedPayee }: RecordPaymentModalProps) {
  const navigate = useNavigate();
  const [payee, setPayee] = useState(selectedPayee || "");

  const selectedPayeeInfo = payees.find(p => p.name === payee);
  const owed = selectedPayeeInfo ? selectedPayeeInfo.agreed - selectedPayeeInfo.paid : 0;

  const form = useForm({
    initialValues: {
      amount: "",
      completesPayment: false,
    },
  });

  const handleSubmit = form.onSubmit(() => {
    // For now, redirect to actuals
    notifications.show({
      title: "Coming Soon!",
      message: "Simple payment recording is being built. For now, let's use the detailed tracking.",
      color: "blue",
    });
    onClose();
    navigate({ to: "/projects/$projectId/actuals", params: { projectId } } as any);
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Record Payment"
      size="md"
      centered
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <Select
            label="Who did you pay?"
            placeholder="Select person/company"
            data={payees.map(p => ({ value: p.name, label: p.name }))}
            value={payee}
            onChange={(v) => setPayee(v || "")}
            searchable
            clearable
          />

          {selectedPayeeInfo && (
            <Paper p="sm" withBorder bg="gray.0">
              <Group justify="space-between">
                <div>
                  <Text size="xs" c="dimmed">Agreed</Text>
                  <Text fw={600}>{formatCurrencySimple(selectedPayeeInfo.agreed)}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Already Paid</Text>
                  <Text fw={600}>{formatCurrencySimple(selectedPayeeInfo.paid)}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Still Owe</Text>
                  <Text fw={600} c="orange">{formatCurrencySimple(owed)}</Text>
                </div>
              </Group>
            </Paper>
          )}

          <NumberInput
            label="Amount paying now"
            placeholder="0"
            prefix="$"
            thousandSeparator=","
            min={0}
            {...form.getInputProps("amount")}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              Record Payment
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function formatCurrencySimple(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}
